import type { ContextBlock, PipelineStage } from '../types';
import { draftText } from '../draft-text';
import { getPathAliases, savePathAliases } from '../../db/settings-repo';

const MIN_PREFIX_CHARS = 8; // shorter prefixes aren't worth an alias indirection

/** Longest shared leading directory segments across `paths` (always keeps at least the filename unaliased). */
function longestCommonDirPrefix(paths: string[]): string {
  if (paths.length < 2) return '';
  const segs = paths.map((p) => p.split('/'));
  const minLen = Math.min(...segs.map((s) => s.length));
  const common: string[] = [];
  for (let i = 0; i < minLen - 1; i++) {
    const seg = segs[0][i];
    if (segs.every((s) => s[i] === seg)) common.push(seg);
    else break;
  }
  return common.length ? common.join('/') + '/' : '';
}

function aliasFor(prefix: string): string {
  const segments = prefix.replace(/\/$/, '').split('/');
  return `@${segments[segments.length - 1]}/`;
}

/** Replaces `prefix` with `alias` in labels (always) and, for raw file blocks, only in our own generated header lines (never inside real file content, so imports/strings in the actual code are never rewritten). */
function aliasBlock(block: ContextBlock, prefix: string, alias: string): ContextBlock {
  const label = block.label.startsWith(prefix) ? alias + block.label.slice(prefix.length) : block.label;
  const isRawFile = block.id.startsWith('file:');
  const content = isRawFile
    ? block.content
        .split('\n')
        .map((line) => (line.startsWith('#') && line.includes(prefix) ? line.split(prefix).join(alias) : line))
        .join('\n')
    : block.content.split(prefix).join(alias);
  return { ...block, label, content };
}

export const e2PathAliasing: PipelineStage = {
  id: 'E2',
  group: 'E',
  label: 'Path aliasing',
  description: 'Replace long repeated path prefixes with short aliases in outgoing content.',
  impact: 'Low',
  defaultEnabled: true,
  implemented: true,

  async run(draft, _params, ctx) {
    const tokensBefore = ctx.estimateTokens(draftText(draft));

    const aliases = await getPathAliases();
    const paths = draft.openFiles.map((f) => f.path);
    const detectedPrefix = longestCommonDirPrefix(paths);

    if (detectedPrefix.length >= MIN_PREFIX_CHARS && !aliases[detectedPrefix]) {
      aliases[detectedPrefix] = aliasFor(detectedPrefix);
      void savePathAliases(aliases); // persist for reuse on future turns; failure here doesn't affect this request
    }

    // Require 2+ *currently* open files to actually share a prefix before applying it — an alias
    // persisted from an earlier project/session shouldn't get applied to a lone open file just
    // because its path happens to start with the same string (e.g. a generic "src/" segment);
    // that would add the alias-definition overhead in the system prompt for zero real savings.
    const activePrefixes = Object.entries(aliases).filter(
      ([prefix]) => paths.filter((p) => p.startsWith(prefix)).length >= 2,
    );
    if (activePrefixes.length === 0) {
      return {
        draft,
        report: {
          stageId: 'E2',
          tokensBefore,
          tokensAfter: tokensBefore,
          tokensSaved: 0,
          notes: ['No repeated path prefix long enough to alias (need 2+ open files sharing a directory).'],
        },
      };
    }

    let newBlocks = draft.contextBlocks;
    for (const [prefix, alias] of activePrefixes) {
      newBlocks = newBlocks.map((b) => aliasBlock(b, prefix, alias));
    }

    const aliasNote = activePrefixes.map(([prefix, alias]) => `"${alias}" = "${prefix}"`).join(', ');
    const newDraft = {
      ...draft,
      contextBlocks: newBlocks,
      systemPrompt: `${draft.systemPrompt}\n\nPath aliases in the context below: ${aliasNote}`,
    };
    const tokensAfter = ctx.estimateTokens(draftText(newDraft));

    return {
      draft: newDraft,
      report: {
        stageId: 'E2',
        tokensBefore,
        tokensAfter,
        tokensSaved: Math.max(0, tokensBefore - tokensAfter),
        notes: [`Aliased ${activePrefixes.length} repeated path prefix(es): ${aliasNote}.`],
      },
    };
  },
};
